import { useEffect, useState } from "react";
import { Link } from "react-router";
import { googleStartUrl } from "../../lib/api.ts";
import { loadRuntimeConfig } from "../../lib/runtime-config.ts";
import { useSession } from "../../lib/session.tsx";
import { GoogleMark } from "./GoogleMark.tsx";
import { readTeacherInviteCode, teacherInviteStartUrl } from "./teacher-invite.ts";

/**
 * Страница приглашения преподавателя, `/teacher-invite#<код>` (раздел 9
 * `docs/admin-panel-design.md`).
 *
 * Отдельной ручки погашения нет: кнопка начинает обычный вход через Google с
 * кодом, сервер кладёт код в state-cookie и гасит его в callback (9.5). Отказ
 * (истекло, отозвано, уже использовано) возвращается на корень параметром
 * `auth_error`, и его текст живёт в общем словаре `auth-errors.ts`.
 *
 * ⚠️ Фрагмент здесь НЕ стирается, в отличие от `/view`. Код просмотра гасится
 * при открытии страницы, а этот только после Google: стёртый фрагмент означал
 * бы, что перезагрузка страницы до нажатия теряет приглашение. Наружу фрагмент
 * не уходит ничем (шапка `teacher-invite.ts`).
 *
 * Google здесь ссылка, а не кнопка с обработчиком, по той же причине, что на
 * экране входа: сервер отвечает редиректом на чужой домен.
 */
export function TeacherInvitePage() {
  const { state, signOut } = useSession();
  const [leaving, setLeaving] = useState(false);
  const [code] = useState(() => readTeacherInviteCode(window.location.hash));
  // Пока конфиг не приехал, кнопки нет: мигнувшая и исчезнувшая хуже
  const [google, setGoogle] = useState<"loading" | "off" | string>("loading");

  useEffect(() => {
    if (code === null) return;
    let alive = true;
    loadRuntimeConfig()
      .then(async (cfg) => {
        if (!alive) return;
        if (!cfg.googleSignInEnabled) {
          setGoogle("off");
          return;
        }
        const start = await googleStartUrl();
        if (alive) setGoogle(teacherInviteStartUrl(start, code));
      })
      .catch(() => {
        if (alive) setGoogle("off");
      });
    return () => {
      alive = false;
    };
  }, [code]);

  return (
    <main className="lobby">
      <h1>Приглашение для преподавателя</h1>
      {code === null ? (
        <p className="err">
          Ссылка неполная. Откройте её заново из сообщения, в котором её прислали.
        </p>
      ) : state.status === "signed" ? (
        <>
          {/*
            Живая сессия бывает и чужой: гостевой ученик на общем компьютере,
            просмотр кабинета у админа. Выход тем же `signOut`, что в кабинете;
            после него провайдер ставит `anon`, и страница рисует кнопку входа с
            кодом, который уже прочитан из фрагмента и лежит в состоянии.
          */}
          <p>Вы уже вошли. Если это ваш кабинет, приглашение больше не нужно.</p>
          <p className="muted">Если кабинет не ваш, выйдите и войдите по приглашению.</p>
          <div className="lobby__row">
            <Link className="btn" to="/" replace>
              В кабинет
            </Link>
            <button
              type="button"
              className="btn"
              disabled={leaving}
              onClick={() => {
                setLeaving(true);
                void signOut().finally(() => setLeaving(false));
              }}
            >
              Выйти
            </button>
          </div>
        </>
      ) : (
        <>
          <p>
            Вас пригласили завести кабинет преподавателя. Войдите через Google, пароль
            придумывать не нужно.
          </p>
          {google === "off" ? (
            <p className="err">
              Вход через Google сейчас выключен. Напишите тому, кто прислал приглашение.
            </p>
          ) : google === "loading" ? null : (
            <div className="lobby__providers">
              <a className="provider" href={google}>
                <GoogleMark />
                Войти через Google
              </a>
            </div>
          )}
          <p className="muted">По одной ссылке заводится один кабинет.</p>
        </>
      )}
    </main>
  );
}
